import React, { useRef, useState } from "react"

import { Skeleton } from "@coval/ui/skeleton"

import type {
  AnnotationRenderContext,
  WordHighlight,
} from "@/components/transcript/word-highlight"
import type { Message as MessageType } from "@/types/Transcript"

import { CopyTranscriptButton } from "./CopyTranscriptButton"
import { Message } from "./Message"
import { isMessageDimmed } from "./messageDimming"
import {
  isWebsocketEventMessage,
  SystemEventMessage,
} from "./SystemEventMessage"
import { TranscriptContainer } from "./TranscriptContainer"

interface ConversationMessageComponentProps {
  message: MessageType
  index: number
  isSelected?: boolean
  onClick?: (index: number | null) => void
  className?: string
  highlightedMessages?: number[] | null
  highlightedWords?: WordHighlight[] | null
  metricRecordForAnnotations?: AnnotationRenderer
  agentLabel?: string
  userLabel?: string
  agentBgColor?: string
  userBgColor?: string
  agentAvatarUrl?: string
  userAvatarUrl?: string
  agentAvatarTint?: string
  userAvatarTint?: string
  latencySeconds?: number
}

interface ConversationViewerProps {
  transcript: MessageType[]
  className?: string
  showCopyButton?: boolean
  onMessageClick?: (index: number | null) => void
  selectedMessageIndex?: number | null
  highlightedMessages?: number[] | null
  highlightedWords?: WordHighlight[] | null
  metricRecordForAnnotations?: AnnotationRenderer
  isLoading?: boolean
  // Allow custom message component for features like annotations
  MessageComponent?: React.ComponentType<ConversationMessageComponentProps>
  agentLabel?: string
  userLabel?: string
  agentBgColor?: string
  userBgColor?: string
  agentAvatarUrl?: string
  userAvatarUrl?: string
  agentAvatarTint?: string
  userAvatarTint?: string
  /**
   * The element that actually scrolls is the viewer's inner region, not its
   * root — callers that follow or track scrolling (e.g. the live transcript's
   * pinned-to-bottom behavior) reach it through these.
   */
  scrollRef?: React.Ref<HTMLDivElement>
  onScroll?: React.UIEventHandler<HTMLDivElement>
  /**
   * Rendered directly below each message, outside its bubble. For a caller
   * that hangs its own detail off a message — trace spans, for one — without
   * changing how the message itself reads.
   */
  renderMessageFooter?: (message: MessageType, index: number) => React.ReactNode
  /** Rendered above the first message, for anything covering the whole call. */
  header?: React.ReactNode
  /** Rendered below the last message, for how the call ended. */
  footer?: React.ReactNode
  /**
   * Response latency in seconds, keyed by the index of the message each gap
   * ended. Shown on those bubbles while a latency metric is open.
   */
  messageLatencies?: ReadonlyMap<number, number> | null
}

interface AnnotationRenderer {
  renderAnnotation: (
    context: AnnotationRenderContext,
    key: string,
  ) => React.ReactNode
}

export const ConversationViewer: React.FC<ConversationViewerProps> = ({
  transcript,
  className = "",
  showCopyButton = true,
  onMessageClick,
  selectedMessageIndex = null,
  highlightedMessages = null,
  highlightedWords = null,
  metricRecordForAnnotations,
  isLoading = false,
  MessageComponent = Message as React.ComponentType<ConversationMessageComponentProps>,
  agentLabel = "My Agent",
  userLabel = "Coval",
  agentBgColor,
  userBgColor,
  agentAvatarUrl,
  userAvatarUrl,
  agentAvatarTint,
  userAvatarTint,
  scrollRef,
  onScroll,
  renderMessageFooter,
  header,
  footer,
  messageLatencies,
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const [internalSelectedIndex, setInternalSelectedIndex] = useState<
    number | null
  >(null)

  const handleMessageClick = (messageIndex: number | null) => {
    if (onMessageClick) {
      onMessageClick(messageIndex)
    } else {
      setInternalSelectedIndex(messageIndex)
    }
  }

  const handleContainerClick = (e: React.MouseEvent) => {
    // Check if the click was on the container itself (not on a message bubble)
    if (e.target === e.currentTarget) {
      if (onMessageClick) {
        onMessageClick(-1) // Signal deselection to parent
      } else {
        setInternalSelectedIndex(null)
      }
    }
  }

  const handleCopyTranscript = async () => {
    if (!transcript || transcript.length === 0) {
      return
    }

    try {
      const transcriptText = JSON.stringify(transcript, null, 2)
      await navigator.clipboard.writeText(transcriptText)
    } catch {
      // Silent fail
    }
  }

  const currentSelectedIndex =
    selectedMessageIndex !== null ? selectedMessageIndex : internalSelectedIndex

  if (isLoading) {
    const skeletonAlignments = [true, false, true, false, true]
    return (
      <div className="p-4 space-y-4">
        {skeletonAlignments.map((isUser, i) => (
          <div key={i} className={`flex ${isUser ? "" : "justify-end"}`}>
            <div className="w-3/4 rounded-lg p-3 space-y-2 bg-surface-1">
              <Skeleton className="h-2.5 w-3/4 rounded" />
              <Skeleton className="h-2.5 w-1/2 rounded" />
            </div>
          </div>
        ))}
      </div>
    )
  }

  if (!transcript || transcript.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full opacity-60 px-6">
        <svg
          className="w-12 h-12 mb-3 opacity-40"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
          />
        </svg>
        <p className="text-sm font-medium">No transcript available</p>
      </div>
    )
  }

  return (
    <div
      ref={scrollContainerRef}
      className={`flex flex-col h-full min-h-0 ${className}`}
    >
      {showCopyButton && (
        <div className="flex justify-between items-center px-4 pt-2 pb-2">
          <h3 className="text-sm font-semibold text-coval-text">Transcript</h3>
          <CopyTranscriptButton
            onCopy={() => {
              void handleCopyTranscript()
            }}
          />
        </div>
      )}

      <div
        role="presentation"
        ref={scrollRef}
        className="flex-1 overflow-y-auto px-0 relative"
        onClick={handleContainerClick}
        onScroll={onScroll}
      >
        <TranscriptContainer>
          <div
            role="presentation"
            className="space-y-2"
            onClick={handleContainerClick}
          >
            {header}
            {transcript.map((message: MessageType, index: number) => {
              // Tool-call-only assistant entries have no text content, but
              // still carry useful transcript information.
              const hasToolCalls =
                Array.isArray(message.tool_calls) &&
                message.tool_calls.length > 0

              // Skip rendering if neither content nor tool calls are present.
              if (
                (!message.content ||
                  (typeof message.content === "string" &&
                    message.content.trim() === "")) &&
                !hasToolCalls
              ) {
                return null
              }

              // Collapse here rather than inside a message renderer: callers
              // swap MessageComponent for annotation-aware variants, and an
              // annotation can't apply to a websocket event anyway.
              if (isWebsocketEventMessage(message)) {
                const isDimmed = isMessageDimmed(
                  highlightedMessages,
                  index,
                  currentSelectedIndex === index,
                )

                return (
                  <SystemEventMessage
                    key={index}
                    message={message}
                    index={index}
                    isSelected={currentSelectedIndex === index}
                    onClick={handleMessageClick}
                    className={isDimmed ? "opacity-45" : ""}
                  />
                )
              }

              const messageNode = (
                <MessageComponent
                  key={index}
                  message={message}
                  index={index}
                  isSelected={currentSelectedIndex === index}
                  onClick={handleMessageClick}
                  highlightedMessages={highlightedMessages}
                  highlightedWords={highlightedWords}
                  metricRecordForAnnotations={metricRecordForAnnotations}
                  agentLabel={agentLabel}
                  userLabel={userLabel}
                  agentBgColor={agentBgColor}
                  userBgColor={userBgColor}
                  agentAvatarUrl={agentAvatarUrl}
                  userAvatarUrl={userAvatarUrl}
                  agentAvatarTint={agentAvatarTint}
                  userAvatarTint={userAvatarTint}
                  latencySeconds={messageLatencies?.get(index)}
                />
              )

              const footer = renderMessageFooter?.(message, index)
              if (!footer) {
                return messageNode
              }

              return (
                <div key={index}>
                  {messageNode}
                  {footer}
                </div>
              )
            })}
            {footer}
          </div>
        </TranscriptContainer>
      </div>
    </div>
  )
}
